import React, { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Input from '../../../../components/Input';
import Dropdown from '../../../../components/Dropdown';
import { VALIDATION_FIELD_MAPPING } from '../../../../utils/appConstant';
import { MonthlyStockFilters } from '../../../../interfaces/monthlyStock.interface';

interface FilterProps {
  dropdownSearchData: any;
  initialFilters?: MonthlyStockFilters;
  onFilterChange: (filters: MonthlyStockFilters) => void;
  onDropdownSearchChange: (e: any, name: string) => void;
  monthOptions?: any[];
  yearOptions?: any[];
  stockistOptions?: any[];
  allStockistOptions?: any[];
  branchOptions?: any[];
  allBranchOptions?: any[];
  userOptions?: any[];
  allUserOptions?: any[];
  statusOptions?: any[];
  allStatusOptions?: any[];
}

const defaultFilters: MonthlyStockFilters = {
  searchText: '',
  month: '',
  year: '',
  stockist: '',
  branch: '',
  user: '',
  status: '',
};

const Filters: React.FC<FilterProps> = ({
  dropdownSearchData,
  initialFilters,
  onFilterChange,
  onDropdownSearchChange,
  monthOptions = [],
  yearOptions = [],
  stockistOptions = [],
  allStockistOptions = [],
  branchOptions = [],
  allBranchOptions = [],
  userOptions = [],
  allUserOptions = [],
  statusOptions = [],
  allStatusOptions = [],
}) => {
  const { t } = useTranslation();

  const [filters, setFilters] = useState<MonthlyStockFilters>(initialFilters || defaultFilters);

  useEffect(() => {
    if (initialFilters) {
      setFilters(initialFilters);
    }
  }, [initialFilters]);

  const handleInputChange = useCallback(
    (event: any, name: string) => {
      setFilters((prev) => {
        const newFilters = { ...prev, [name]: event.target.value };
        if (name !== VALIDATION_FIELD_MAPPING.searchText) {
          onFilterChange(newFilters);
        }
        return newFilters;
      });
    },
    [onFilterChange]
  );

  const handleKeyDown = useCallback(
    (e: any) => {
      if (e.key === 'Enter') {
        onFilterChange(filters);
      }
    },
    [filters, onFilterChange]
  );

  const handleOnBlur = useCallback(
    (_e: any) => {
      onFilterChange(filters);
    },
    [filters, onFilterChange]
  );

  return (
    <div className="row mb-6">
      <div className="col-sm-12">
        <div className="card">
          <div className="card-body">

            {/* Row 1: Dropdown Filters */}
            <div className="row g-3 align-items-end mb-3">

              {/* Month */}
              <div className="col-md col-sm-6">
                <Dropdown
                  id={VALIDATION_FIELD_MAPPING.month}
                  name={VALIDATION_FIELD_MAPPING.month}
                  values={filters.month ? [filters.month] : []}
                  options={monthOptions.filter(o => !dropdownSearchData.month || o.label.toLowerCase().includes(dropdownSearchData.month.toLowerCase()))}
                  allOptions={monthOptions}
                  placeholder={t('monthlyStock.list.pageTexts.filters.placeholders.month')}
                  label={t('monthlyStock.list.pageTexts.filters.month')}
                  searchText={dropdownSearchData.month}
                  onSearch={onDropdownSearchChange}
                  onChange={(e: any) => {
                    const value = Array.isArray(e.target?.value) ? e.target.value[0] : e.target?.value || '';
                    handleInputChange({ target: { value } }, VALIDATION_FIELD_MAPPING.month);
                  }}
                />
              </div>

              {/* Year */}
              <div className="col-md col-sm-6">
                <Dropdown
                  id={VALIDATION_FIELD_MAPPING.year}
                  name={VALIDATION_FIELD_MAPPING.year}
                  values={filters.year ? [filters.year] : []}
                  options={yearOptions.filter(o => !dropdownSearchData.year || o.label.includes(dropdownSearchData.year))}
                  allOptions={yearOptions}
                  placeholder={t('monthlyStock.list.pageTexts.filters.placeholders.year')}
                  label={t('monthlyStock.list.pageTexts.filters.year')}
                  searchText={dropdownSearchData.year}
                  onSearch={onDropdownSearchChange}
                  onChange={(e: any) => {
                    const value = Array.isArray(e.target?.value) ? e.target.value[0] : e.target?.value || '';
                    handleInputChange({ target: { value } }, VALIDATION_FIELD_MAPPING.year);
                  }}
                />
              </div>

              {/* Stockist */}
              <div className="col-md col-sm-6">
                <Dropdown
                  id={VALIDATION_FIELD_MAPPING.stockist}
                  name={VALIDATION_FIELD_MAPPING.stockist}
                  values={filters.stockist ? [filters.stockist] : []}
                  options={stockistOptions}
                  allOptions={allStockistOptions}
                  placeholder={t('monthlyStock.list.pageTexts.filters.placeholders.stockist')}
                  label={t('monthlyStock.list.pageTexts.filters.stockist')}
                  searchText={dropdownSearchData.stockist}
                  onSearch={onDropdownSearchChange}
                  onChange={(e: any) => {
                    const value = Array.isArray(e.target?.value) ? e.target.value[0] : e.target?.value || '';
                    setFilters((prev) => {
                      const newFilters = { ...prev, [VALIDATION_FIELD_MAPPING.stockist]: value, [VALIDATION_FIELD_MAPPING.branch]: '' };
                      onFilterChange(newFilters);
                      return newFilters;
                    });
                  }}
                />
              </div>

              {/* Branch */}
              <div className="col-md col-sm-6">
                <Dropdown
                  id={VALIDATION_FIELD_MAPPING.branch}
                  name={VALIDATION_FIELD_MAPPING.branch}
                  values={filters.branch ? [filters.branch] : []}
                  options={branchOptions}
                  allOptions={allBranchOptions}
                  placeholder={t('monthlyStock.list.pageTexts.filters.placeholders.branch')}
                  label={t('monthlyStock.list.pageTexts.filters.branch')}
                  searchText={dropdownSearchData.branch}
                  onSearch={onDropdownSearchChange}
                  onChange={(e: any) => {
                    const value = Array.isArray(e.target?.value) ? e.target.value[0] : e.target?.value || '';
                    handleInputChange({ target: { value } }, VALIDATION_FIELD_MAPPING.branch);
                  }}
                />
              </div>

              {/* User */}
              <div className="col-md col-sm-6">
                <Dropdown
                  id={VALIDATION_FIELD_MAPPING.user}
                  name={VALIDATION_FIELD_MAPPING.user}
                  values={filters.user ? [filters.user] : []}
                  options={userOptions}
                  allOptions={allUserOptions}
                  placeholder={t('monthlyStock.list.pageTexts.filters.placeholders.user')}
                  label={t('monthlyStock.list.pageTexts.filters.user')}
                  searchText={dropdownSearchData.user}
                  onSearch={onDropdownSearchChange}
                  onChange={(e: any) => {
                    const value = Array.isArray(e.target?.value) ? e.target.value[0] : e.target?.value || '';
                    handleInputChange({ target: { value } }, VALIDATION_FIELD_MAPPING.user);
                  }}
                />
              </div>

              {/* Status */}
              <div className="col-md col-sm-6">
                <Dropdown
                  id={VALIDATION_FIELD_MAPPING.status}
                  name={VALIDATION_FIELD_MAPPING.status}
                  values={filters.status ? [filters.status] : []}
                  options={statusOptions}
                  allOptions={allStatusOptions}
                  placeholder={t('monthlyStock.list.pageTexts.filters.placeholders.status')}
                  label={t('monthlyStock.list.pageTexts.filters.status')}
                  searchText={dropdownSearchData.status}
                  onSearch={onDropdownSearchChange}
                  onChange={(e: any) => {
                    const value = Array.isArray(e.target?.value) ? e.target.value[0] : e.target?.value || '';
                    handleInputChange({ target: { value } }, VALIDATION_FIELD_MAPPING.status);
                  }}
                />
              </div>
            </div>

            {/* Row 2: Search */}
            <div className="row g-3">
              <div className="col-md-6 col-sm-12">
                <Input
                  type="search"
                  id={VALIDATION_FIELD_MAPPING.searchText}
                  name={VALIDATION_FIELD_MAPPING.searchText}
                  placeholder={t('monthlyStock.list.pageTexts.filters.searchPlaceholder')}
                  value={filters.searchText}
                  onBlur={handleOnBlur}
                  onKeyDown={handleKeyDown}
                  onChange={(e) => handleInputChange(e, VALIDATION_FIELD_MAPPING.searchText)}
                />
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default Filters;



